import React, { useState } from "react";
import {
  Button,
  Flex,
  Text,
  Tooltip,
  Heading,
  IconButton,
} from "@chakra-ui/react";

import PermissionSelector from "./PermissionSelector";
import { FaArrowLeftLong } from "react-icons/fa6";
import {
  backgroundLight,
  buttonRed,
  buttonRedText,
  newBorder,
  primaryAlt,
  secondary,
  textPrimary,
  textSecondary,
  white,
} from "../../../../../utils/colors";
import { useTranslation } from "react-i18next";
import FormControlDefault from "../../../../../components/input/FormControlDefault";
import FormControlRole from "../../../../../components/input/FormControlRole";
import { updateTenantUserPermissionsAction } from "../../../../../actions/tenantActions";
import { useDispatch } from "react-redux";
import StatusButtonNew from "../../../../../components/buttons/StatusButtonNew";
import ConfirmStatusModal from "../../../../../components/modals/ConfirmStatusModal";

const EditUser = ({ close, user, loading, slug }) => {
  const { t } = useTranslation();

  // STATE
  const [email, setEmail] = useState(user?.email || "");
  const [role, setRole] = useState(user?.role || "user");
  const [permissions, setPermissions] = useState(user?.permissions || []);
  const [status, setStatus] = useState(user?.status || "active");

  const [showStatusWarning, setShowStatusWarning] = useState(false);

  // Email validation: Standard email format
  const isEmailEmpty = email === "";
  const isEmailInvalid =
    !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(email) && !isEmailEmpty;

  // TOOLTIP VALIDATION MESSAGES
  const tooltipMessages = [];

  // Email error messages
  if (isEmailEmpty) {
    tooltipMessages.push(t("formError.emailRequired"));
  } else if (isEmailInvalid) {
    tooltipMessages.push(t("formError.emailInvalid"));
  }

  // REDUX
  const dispatch = useDispatch();

  const updateUserHandler = async () => {
    await dispatch(
      updateTenantUserPermissionsAction(
        slug,
        user._id,
        role,
        permissions,
        status
      )
    );

    close();
  };

  // FUNCTIONS
  console.log("user", user);
  console.log("status", status);

  return (
    <>
      {/* MODALS  */}
      {showStatusWarning && (
        <ConfirmStatusModal
          status={user?.status}
          modalTitle={t("user.changeStatusTitle")}
          title={t("user.changeStatusTitle")}
          subtitle={
            user?.status !== "active"
              ? t("user.changeStatusTextActive")
              : t("user.changeStatusTextInactive")
          }
          isOpen={showStatusWarning}
          onClose={() => setShowStatusWarning(false)}
          confirmAction={() => {
            // console.log("Delete action triggered");
            setStatus((prev) => (prev === "active" ? "inactive" : "active"));
            setShowStatusWarning(false);
          }}
        />
      )}

      <Flex w="100%" h="100%" direction="column">
        {/* HEADER START */}
        <Flex
          justify="space-between"
          align="center"
          p="1.25rem 2rem 1.25rem"
          borderBottom={`1px solid ${newBorder}`}
          mb="0rem"
          position="relative"
          zIndex={90}
          w="100%"
        >
          <Flex align="center">
            <IconButton
              bg={backgroundLight}
              border={`1px solid ${backgroundLight}`}
              color={textSecondary}
              icon={<FaArrowLeftLong />}
              borderRadius="10.75rem"
              fontSize="0.875rem"
              onClick={close}
              mr="1rem"
              _hover={{
                bg: primaryAlt,
                color: white,
                border: primaryAlt,
              }}
            />

            <Flex align="flex-start" direction="column">
              <Heading
                fontSize="1.125rem"
                color={textPrimary}
                fontWeight="600"
                mr={4}
              >
                {t("user.editUser")}
              </Heading>
            </Flex>
          </Flex>

          <Flex align="center" justify="flex-end" gap="1rem">
            <Tooltip
              p="0.5rem 1rem"
              borderRadius="1rem"
              label={
                <Flex direction="column" justify="center" align="center">
                  <Text fontSize="xs" fontWeight="500" textAlign="center">
                    {t("patient.newStatusExplanation")}
                  </Text>
                </Flex>
              }
            ></Tooltip>

            <StatusButtonNew
              isSmall
              currentStatus={status}
              p="0 2rem"
              options="employee"
              onUpdateStatus={() => {
                // setStatus(user?.status);
                setShowStatusWarning(true);
              }}
            />
          </Flex>
        </Flex>
        {/* HEADER END */}

        {/* MAIN START */}

        <Flex
          w="100%"
          h="100%"
          direction="column"
          overflowY="auto"
          p="1rem 2rem 7rem"
        >
          {/* PATIENT DETAILS START */}
          <Flex direction="column" w="100%">
            <Text
              fontSize="0.875rem"
              fontWeight="600"
              color={textPrimary}
              m="0 0 0.5rem"
            >
              {t("user.userDetails")}
            </Text>
            <Flex
              direction="column"
              w="100%"
              bg={backgroundLight}
              borderRadius="1rem"
              p="1.25rem 1.5rem 0.25rem"
              m="0 0 1.5rem"
            >
              <Flex
                gap={["0rem", "2rem", "2rem"]}
                w="100%"
                direction={["column", "row", "row"]}
              >
                <FormControlDefault
                  type="email"
                  size="lg"
                  labelFontSize="0.8rem"
                  label={t("form.email")}
                  value={email}
                  placeholder={t("placeholder.email")}
                  onChange={(e) => {
                    setEmail(e.target.value);
                  }}
                  w={["100%", "60%", "60%"]}
                  errorMessage={t("formError.emailRequired")}
                  isDisabled={true}
                  isLabelEnabled={true}
                />
                <FormControlRole
                  isRequired
                  isSearchable={false}
                  label={t("form.role")}
                  labelFontSize="0.8rem"
                  value={role}
                  placeholder={t("placeholder.role")}
                  onChange={(selectedOption) => {
                    setRole(selectedOption.value);
                  }}
                  w="40%"
                />
              </Flex>
            </Flex>
          </Flex>
          <Flex direction="column" w="100%">
            <Text
              fontSize="0.875rem"
              fontWeight="600"
              color={textPrimary}
              m="0 0 0.5rem"
            >
              {t("user.rolePermissions")}
            </Text>
            <Flex
              direction="column"
              w="100%"
              bg={backgroundLight}
              borderRadius="1rem"
              p="1.25rem 1.5rem 0.25rem"
            >
              <Flex
                gap={["0rem", "2rem", "2rem"]}
                w="100%"
                direction={["column", "row", "row"]}
              >
                <PermissionSelector
                  role={role}
                  setRole={setRole}
                  permissions={permissions}
                  setPermissions={setPermissions}
                />
              </Flex>
            </Flex>
          </Flex>
        </Flex>

        {/* MAIN END */}
      </Flex>

      <Flex
        align="center"
        justify="flex-end"
        position="fixed"
        bottom="2rem"
        right="2rem"
        gap="1rem"
        zIndex={9000}
      >
        <Tooltip
          bg={buttonRed}
          color={buttonRedText}
          padding="0.5rem 1rem"
          borderRadius="1rem"
          placement="top"
          label={
            tooltipMessages.length > 0 ? (
              <Flex direction="column" justify="center" align="center">
                {tooltipMessages.map((message, index) => (
                  <Text
                    key={index}
                    color={buttonRedText}
                    fontSize="xs"
                    fontWeight="500"
                    textAlign="center"
                  >
                    {message}
                  </Text>
                ))}
              </Flex>
            ) : null
          }
          isDisabled={tooltipMessages.length === 0} // Disable tooltip if no errors
        >
          <Button
            minWidth="10rem"
            bg={textPrimary}
            size="lg"
            color={white}
            fontSize="md"
            borderRadius="10rem"
            border={`1px solid ${white}`}
            _hover={{
              bg: secondary,
              color: white,
              border: `1px solid ${white}`,
            }}
            isDisabled={tooltipMessages.length > 0} // Disable if any errors exist
            isLoading={loading}
            onClick={updateUserHandler}
          >
            {t("common.saveChanges")}
          </Button>
        </Tooltip>
      </Flex>
    </>
  );
};

export default EditUser;
